import './PackUI.scss';

const cents = amount => Math.max(0, Math.round(Number(amount || 0) * 100));
const money = amountInCents => (Math.max(0, amountInCents) / 100).toFixed(2);

const fixedInstallmentDiscounts = (installments, discountCents, allocation) => {
  const rows = installments.map(installment => ({
    amountCents: cents(installment?.price),
    eligible: installment?.allowDiscount !== false,
    discountCents: 0,
  }));
  const eligibleIndexes = rows
    .map((row, index) => (row.eligible && row.amountCents > 0 ? index : -1))
    .filter(index => index >= 0);
  const eligibleTotal = eligibleIndexes.reduce(
    (total, index) => total + rows[index].amountCents,
    0
  );
  let remaining = Math.min(eligibleTotal, discountCents);

  if (!eligibleIndexes.length || remaining === 0) return rows;

  if (allocation === 'first_eligible') {
    eligibleIndexes.forEach(index => {
      const applied = Math.min(remaining, rows[index].amountCents);
      rows[index].discountCents = applied;
      remaining -= applied;
    });
    return rows;
  }

  const totalDiscount = remaining;
  if (allocation === 'equal') {
    const equalShare = Math.floor(totalDiscount / eligibleIndexes.length);
    eligibleIndexes.forEach((index, offset) => {
      const withRemainder =
        offset === eligibleIndexes.length - 1
          ? equalShare + (totalDiscount - equalShare * eligibleIndexes.length)
          : equalShare;
      const applied = Math.min(withRemainder, rows[index].amountCents);
      rows[index].discountCents = applied;
      remaining -= applied;
    });
    eligibleIndexes
      .slice()
      .reverse()
      .forEach(index => {
        const capacity = rows[index].amountCents - rows[index].discountCents;
        const applied = Math.min(capacity, remaining);
        rows[index].discountCents += applied;
        remaining -= applied;
      });
    return rows;
  }

  eligibleIndexes.forEach((index, offset) => {
    const applied =
      offset === eligibleIndexes.length - 1
        ? remaining
        : Math.floor((totalDiscount * rows[index].amountCents) / eligibleTotal);
    rows[index].discountCents = Math.min(applied, rows[index].amountCents, remaining);
    remaining -= rows[index].discountCents;
  });

  return rows;
};

export const packageCardDiscountPreview = ({price, coupon, inst}) => {
  if (!coupon || inst?.allowDiscount === false) return null;

  const originalCents = cents(price);
  const format = coupon.format || 'percent';
  let discountCents = 0;
  let firstPaymentFinalCents = null;

  if (format === 'fixed_amount') {
    const installments = inst?.installments || [];
    if (installments.length) {
      const rows = fixedInstallmentDiscounts(
        installments,
        cents(coupon.amount),
        coupon.installmentAllocation || 'first_eligible'
      );
      discountCents = rows.reduce((total, row) => total + row.discountCents, 0);
      if (rows[0]) {
        firstPaymentFinalCents = rows[0].amountCents - rows[0].discountCents;
      }
    } else {
      discountCents = Math.min(originalCents, cents(coupon.amount));
    }
  } else if (format === 'percent') {
    discountCents = Math.min(
      originalCents,
      Math.max(0, Math.round((originalCents * Number(coupon.percent || 0)) / 100))
    );
    if (inst?.installments?.length) {
      const firstPaymentCents = cents(inst.installments[0]?.price);
      firstPaymentFinalCents = Math.max(
        0,
        firstPaymentCents - Math.round((firstPaymentCents * Number(coupon.percent || 0)) / 100)
      );
    }
  } else {
    // Family discounts depend on the selected household members. Their exact
    // price is shown by the server quote once a package is selected.
    return null;
  }

  if (discountCents <= 0) return null;

  return {
    original: money(originalCents),
    discount: money(discountCents),
    final: money(originalCents - discountCents),
    firstPaymentFinal: firstPaymentFinalCents === null ? null : money(firstPaymentFinalCents),
  };
};

export const hasAppliedFamilyPackageDiscount = coupon =>
  ['family_tiered', 'family_all'].includes(coupon?.format) && coupon?.quote?.valid === true;

const PackUI = props => {
  const {
    label,
    price,
    quantity,
    isSelected,
    isShowBookingDate,
    bookingDate,
    coupon,
    firstPaymentPrice,
    installmentCount,
  } = props || {};
  const packLabel = `${label} ${isShowBookingDate ? `for ${bookingDate}` : ''}`;
  const preview = packageCardDiscountPreview({price, coupon, inst: props?.inst});
  const originalPrice = money(cents(price));
  const showDiscount = Boolean(preview);
  const showFamilyDiscountNotice = hasAppliedFamilyPackageDiscount(coupon);
  const originalFirstPayment = money(cents(firstPaymentPrice));

  return (
    <div className="col">
      <div
        className={`card mb-4 rounded-3 shadow-sm ${isSelected ? 'selected' : ''}`}
        role="button"
        onClick={props._handleOnClick}
      >
        <div className="card-header py-3 bg-dark">
          <h4 className="my-0 fw-normal text-white fs-5 pack-card">{packLabel}</h4>
        </div>
        <div className="card-body">
          <h2
            className={
              'pricing-card-title mb-0' +
              (showDiscount && ' text-decoration-line-through text-danger')
            }
          >
            {(!props.isInst || (props.isInst && installmentCount <= 1)) && <>${originalPrice}</>}
          </h2>
          {props.isInst && installmentCount > 1 ? (
            <>
              <h2 className="pricing-card-title mb-0 font-size-18 text-align-left">
                {installmentCount} Payments
              </h2>
              {firstPaymentPrice && (
                <h2 className="pricing-card-title mb-0 font-size-18">
                  First Payment: $
                  {showDiscount
                    ? preview.firstPaymentFinal || originalFirstPayment
                    : originalFirstPayment}
                  {showDiscount && (
                    <span className="text-decoration-line-through text-danger ms-2">
                      ${originalFirstPayment}
                    </span>
                  )}
                </h2>
              )}
              <h2 className="pricing-card-title mb-0 font-size-12 text-align-left fw-normal">
                Total Payments: ${showDiscount ? preview.final : originalPrice}
                {showDiscount && (
                  <span className="text-decoration-line-through text-danger ms-2">
                    ${originalPrice}
                  </span>
                )}
              </h2>
              <p className="font-size-12">(includes all fees)</p>
            </>
          ) : (
            <p className="font-size-12">(includes all fees)</p>
          )}
          {(!props.isInst || (props.isInst && installmentCount <= 1)) && showDiscount && (
            <h2 className="pricing-card-title mb-0">${preview.final}</h2>
          )}
          {showFamilyDiscountNotice && (
            <div className="pack-card-family-discount-notice">
              Family member discount applied, see details below.
            </div>
          )}
          {quantity && (
            <ul className="list-unstyled mt-3 mb-4">
              <li className="p-1 border-bottom border-top">
                {props.isAppointment
                  ? `${quantity} Session${quantity > 1 ? 's' : ''}`
                  : `${quantity} Class${quantity > 1 ? 'es' : ''}`}
              </li>
            </ul>
          )}
        </div>
        {props.isInst && (
          <div className="card-footer" style={{backgroundColor: '#2596be'}}>
            <div className="text-white fw-normal fs-5 ">
              {props.inst?.isInstallment ? (
                'Installment Payments'
              ) : (
                <span>
                  Single <br className="br-hide" />
                  Payment
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PackUI;
